import {
  NOT_SHIPPED_HOURS,
  paymentKindOf,
  type EscalationRow,
  type Order,
  type ReturnToReceive,
} from "@estelle/domain";

import { reportingNow } from "@/lib/reportClock";
import type { ChangeRow, ShipmentRow } from "@/modules/orders/types";

/** Confirmed, no shipment yet, and placed over `NOT_SHIPPED_HOURS` ago (#528). */
export function isNotShipped(
  order: Pick<Order, "stage" | "placedAt">,
  now: number = reportingNow(),
): boolean {
  return (
    order.stage === "confirmed" &&
    now - new Date(order.placedAt).getTime() >= NOT_SHIPPED_HOURS * 3_600_000
  );
}

/** A row flattened to the field names its module declares. */
export type Projection = Record<string, unknown>;

/**
 * An order flattened for display.
 *
 * Deliberately computed from the row and nothing else. The adapter has a
 * richer projection of the same field names — it can join a customer record
 * and ask whether an invoice exists, because it holds the whole dataset. This
 * one cannot, and must not: a screen that reached for those joins would be
 * reaching past the data-access interface, which is the one thing the
 * fixture invariant exists to prevent.
 *
 * Anything a list shows that needs a join — the customer's name, the
 * warehouse — the adapter returns on the row, and this reads it from there.
 */
export function projectOrder(order: Order): Projection {
  return {
    orderNumber: order.orderNumber,
    placedAt: order.placedAt,
    financialStatus: order.financialStatus,
    fulfilmentStatus: order.fulfilmentStatus,
    itemCount: order.itemCount,
    deliveryStatus: order.deliveryStatus ?? "",
    deliveryMethod: order.deliveryMethod ?? "",
    tags: order.tags,
    total: order.total,
    customerName: order.customerName ?? order.shippingAddress?.name ?? "",
    shippingProvinceCode: order.shippingAddress?.provinceCode ?? "",
    shippingCountryCode: order.shippingAddress?.countryCode ?? "",
    paymentGateway: order.paymentGatewayKey ?? "",
    discountCode: order.discountCodes.join(", "),
    sourceApp: order.sourceApp ?? "",
    warehouse: order.warehouseName ?? "",
    cancelledAt: order.cancelledAt,
    payment: paymentKindOf(order.paymentGatewayKey) ?? "",
    hasOpenFollowUp: order.hasOpenFollowUp,
    rtoReceived: order.rtoReceived,
    escalated: order.escalated,
    hasComments: order.hasComments,
    mentionsMe: order.mentionsMe,
    shipmentStage: order.shipmentStage ?? "",
    shipmentHealth: order.shipmentHealth ?? "",
    lastScanAt: order.lastScanAt,
    attemptCount: order.attemptCount,
    expectedDeliveryDate: order.expectedDeliveryDate,
    stage: order.stage,
    stageManual: order.stageOverride !== null,
    confirmation: order.confirmation ?? "",
    risk: order.risk ?? "",
    overdue: order.overdueHours !== null,
    notShipped: isNotShipped(order),
  };
}

/** A `ShipmentRow` flattened for `SHIPMENT_FIELDS`. */
export function projectShipmentRow(row: ShipmentRow): Projection {
  return {
    orderNumber: row.orderNumber,
    courier: row.courierKey ?? "",
    status: row.status ?? "",
    trackingNumber: row.trackingNumber ?? "",
    destinationState: row.destinationState,
    destinationCountry: row.destinationCountry,
    placedAt: row.orderPlacedAt,
    shippedAt: row.shippedAt,
    deliveredAt: row.deliveredAt,
    stage: row.stage ?? "",
    health: row.health ?? "",
    lastScanAt: row.lastScanAt,
    attemptCount: row.attemptCount,
    expectedDeliveryDate: row.expectedDeliveryDate,
    rtoReceived: row.rtoReceived,
    escalated: row.openEscalation !== null,
  };
}

/**
 * A `ReturnToReceive` flattened for `RETURN_FIELDS`. From the row alone: the
 * adapter has already joined the days, the units and the warehouse onto it.
 */
export function projectReturnRow(row: ReturnToReceive): Projection {
  return {
    orderNumber: row.orderNumber,
    courier: row.courierKey ?? "",
    trackingNumber: row.trackingNumber ?? "",
    stage: row.stage ?? "",
    returningDays: row.returningDays,
    rtoDelivered: row.rtoDelivered,
    itemCount: row.itemCount,
    shopifyStatus: row.shopifyStatus ?? "",
    warehouse: row.warehouseName ?? "",
    payment: row.payment ?? "",
    manual: row.manual !== null,
  };
}

/**
 * An `EscalationRow` flattened for `ESCALATION_FIELDS` (#702). Every field is
 * computed from the row alone, so a filter on any of them is also answerable
 * in the browser (the bar's "not in view" count relies on it).
 */
export function projectEscalationRow(row: EscalationRow): Projection {
  return {
    orderNumber: row.orderNumber,
    awb: row.awb ?? "",
    courier: row.courierKey ?? "",
    kind: row.kind,
    level: row.level,
    status: row.status,
    openedAt: row.openedAt,
    daysOpen: row.daysOpen,
    lastScanAt: row.lastScanAt,
    attempts: row.attempts,
    lastSentAt: row.lastSentAt,
    orderValue: row.orderValue,
    payment: row.payment ?? "",
    outcome: row.outcome ?? "",
  };
}

/** A `ChangeRow` flattened for `CHANGE_FIELDS`. */
export function projectChangeRow(row: ChangeRow): Projection {
  return {
    orderNumber: row.orderNumber,
    occurredAt: row.occurredAt,
    eventType: row.eventType,
    source: row.source,
    actorUserId: row.actorUserId ?? "",
    field: row.field ?? "",
  };
}
